<div class="op-table-toolbar">
    <div class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-gap-4">
        <div>
            @if (showSearch()) {
                <div class="tw-flex tw-items-center tw-gap-2">
                    <p-iconfield styleClass="tw-w-full">
                        <p-inputicon styleClass="pi pi-search" aria-hidden="true" />
                        <input pInputText
                               type="text"
                               [attr.id]="`${tableKey()}-search-input`"
                               [attr.data-test]="`${tableKey()}-search-input`"
                               [attr.aria-describedby]="searchAriaDescribedBy()"
                               class="tw-w-full"
                               [value]="searchText()"
                               [placeholder]="searchPlaceholder()"
                               (input)="searchInput.emit($event)" />
                    </p-iconfield>
                    <ng-content select="[monTableSearchSuffix]"></ng-content>
                </div>
            }
            <ng-content select="[monTableLeft]"></ng-content>
        </div>
        <div class="tw-flex tw-gap-2 tw-justify-end tw-flex-col sm:tw-flex-row tw-flex-wrap">
            @let filterObject = filters();
            @if (filterObject) {
                <mon-filter [filterObject]="filterObject"
                            (filtersApplied)="filtersApplied.emit($event)"
                            (loadMorefilterOptions)="loadMoreFilterOptions.emit($event)"
                            [filterOrder]="filterOrder()"
                            [pageKey]="filterPageKey() || tableKey()" />
            }
            @if (showCustomizeColumns()) {
                <mon-column-selector
                        [tableKey]="tableKey()"
                        [columns]="selectableColumns()"
                        [(selectedColumns)]="selectedColumns" />
            }
            @if (exportConfig()) {
                <p-button
                        [attr.data-test]="`${tableKey()}-create-export-button`"
                        class="tw-flex"
                        variant="outlined"
                        [label]=""
                        (onClick)="openCreateExport()">
                    <mon-svg-icon name="export" />
                    {{ exportButtonLabel() }}
                </p-button>
            }
            <ng-content select="[monTableRight]"></ng-content>
        </div>
    </div>
    <mon-filter-display [filters]="appliedFilters()" />
</div>

@if (searchResultsAnnouncement()) {
    <div
        aria-live="polite"
        aria-atomic="true"
        class="tw-sr-only"
        [attr.data-test]="`${tableKey()}-live-results`"
    >
        {{ searchResultsAnnouncement() }}
    </div>
}

<div class="op-table-wrapper">
    <p-table
            gridKeyboardNavigation
            scrollHeight="flex"
            styleClass="tw-flex-grow"
            [tableStyleClass]="fixedTableLayout() ? 'tw-table-fixed' : ''"
            [attr.data-test]="`${tableKey()}-table`"
            [paginatorDropdownAppendTo]="'body'"
            [paginator]="true"
            [showPageLinks]="showPageLinks()"
            [showFirstLastIcon]="showFirstLastIcon()"
            [responsiveLayout]="'scroll'"
            [scrollable]="true"
            [lazy]="true"

            [value]="value()"
            [selection]="selection()"
            [dataKey]="dataKey()"
            [loading]="loading()"
            [rows]="perPage()"
            [totalRecords]="total()"
            [first]="first()"
            [rowsPerPageOptions]="AVAILABLE_PAGE_SIZE"
            [sortField]="sortField()"
            [sortOrder]="sortOrder()"
            (onSort)="handleSort($event)"
            (onPage)="handlePage($event)"
            (selectionChange)="handleSelectionChange($event)"
    >
        <caption class="sr-only">
            <ng-content select="[monTableCaption]"></ng-content>
        </caption>

        <ng-template #header let-rowHeader>
            <ng-container *ngTemplateOutlet="headerTemplate()!; context: { $implicit: rowHeader }" />
            <tr role="row">
                @if (showSelectionColumn()) {
                    <th scope="col"
                        class="tw-w-12"
                        [attr.data-test]="`${tableKey()}-checkbox-tableHead`">
                        <p-checkbox
                            [binary]="true"
                            [ngModel]="headerCheckboxChecked()"
                            [indeterminate]="headerCheckboxIndeterminate()"
                            [disabled]="selectionDisabled()"
                            [attr.data-test]="`${tableKey()}-select-all-checkbox`"
                            [ariaLabel]="'table.wrapper.selectAllRowsCheckbox' | transloco"
                            (onChange)="handleHeaderCheckboxToggle()" />
                    </th>
                }
                @for(column of displayedColumns(); track column.id) {
                    @if (column.sortable && !column.actionColumn) {
                        <th scope="col"
                            [attr.class]="getColumnWidthClass(column)"
                            [ngClass]="{
                                'table-action-col': column.actionColumn, 
                                'comparison-column': column.cellClass === 'comparison-column',
                                'p-datatable-sortable-column': true,
                                'p-datatable-column-sorted': sortField() === (column.sortField || column.id)
                            }"
                            [attr.data-test]="`${tableKey}-${column.id}-tableHead`"
                            [attr.aria-describedby]="column.tooltip ? `${tableKey()}-${column.id}-tooltip-help` : null"
                            [attr.aria-sort]="sortField() === (column.sortField || column.id)
                                ? (sortOrder() === 1 ? 'ascending' : 'descending')
                                : 'none'"
                            class="sortable-header-cell"
                        >
                            <button type="button"
                                class="sortable-header-button"
                                [attr.aria-label]="'Sort by ' + column.label +
                                    (sortField() === (column.sortField || column.id)
                                        ? (sortOrder() === 1 ? ', currently sorted ascending' : ', currently sorted descending')
                                        : ', not currently sorted')"
                                (click)="handleSortClick($event, column.sortField || column.id)"
                            >
                                <span>{{ column.label }}</span>
                            @if (column.secondaryLabel) {
                                <br>
                                <span class="text-grey">({{ column.secondaryLabel }})</span>
                            }
                                @if (column.tooltip) {
                                    <i
                                        class="pi pi-info-circle tw-text-gray-500 tw-ml-1"
                                        aria-hidden="true"
                                        [pTooltip]="column.tooltip"
                                        [tooltipPosition]="column.tooltipPosition || 'top'"
                                    ></i>
                                    <span
                                        [attr.id]="`${tableKey()}-${column.id}-tooltip-help`"
                                        class="tw-sr-only"
                                    >{{ column.tooltip }}</span>
                                }
                                <p-sortIcon [field]="column.sortField || column.id" aria-hidden="true" />
                            </button>
                        </th>
                    } @else {
                        <th scope="col"
                            role="columnheader"
                            [attr.class]="getColumnWidthClass(column)"
                            [ngClass]="{'table-action-col': column.actionColumn, 'comparison-column': column.cellClass === 'comparison-column'}"
                            [attr.data-test]="`${tableKey}-${column.id}-tableHead`"
                            [attr.aria-describedby]="column.tooltip ? `${tableKey()}-${column.id}-tooltip-help` : null"
                        >
                            <span>{{ column.label }}</span>
                            @if (column.secondaryLabel) {
                                <br>
                                <span class="text-grey">({{ column.secondaryLabel }})</span>
                            }
                            @if (column.tooltip) {
                                <i
                                    class="pi pi-info-circle tw-text-gray-500 tw-ml-1"
                                    aria-hidden="true"
                                    [pTooltip]="column.tooltip"
                                    [tooltipPosition]="column.tooltipPosition || 'top'"
                                ></i>
                                <span
                                    [attr.id]="`${tableKey()}-${column.id}-tooltip-help`"
                                    class="tw-sr-only"
                                >{{ column.tooltip }}</span>
                            }
                        </th>
                    }
                }
            </tr>
        </ng-template>

        <ng-template #body let-rowData let-rowIndex="rowIndex">
            <tr role="row">
                @if (showSelectionColumn()) {
                    <td [attr.data-test]="`${tableKey()}-checkbox-tableCell`">
                        <p-tableCheckbox [value]="rowData" [attr.data-test]="`${tableKey()}-row-checkbox`" />
                    </td>
                }
                @if (cellsTemplate()) {
                    <!-- Less performant -->
                    @for (cell of displayedColumns(); track cell.id) {
                        <td [attr.class]="getColumnWidthClass(cell)"
                            [ngClass]="{'table-action-col': cell.actionColumn, 'comparison-column': cell.cellClass === 'comparison-column'}"
                            [attr.data-test]="`${tableKey}-${cell.id}-tableCell`">
                            @let templateContext = { $implicit: rowData, field: cell.id, index: rowIndex - first() };
                            <ng-container *ngTemplateOutlet="cellsTemplate()!; context: templateContext" />
                        </td>
                    }
                } @else {
                    <!-- More performant but requires monTableCellTemplate directive -->
                    @for (cell of displayedColumns(); track cell.id) {
                        <td [attr.data-test]="`${tableKey}-${cell.id}-tableCell`"
                            [attr.class]="getColumnWidthClass(cell)"
                            [ngClass]="{'table-action-col': cell.actionColumn, 'comparison-column': cell.cellClass === 'comparison-column'}">
                            @if (getCellTemplate(cell.id); as template) {
                                @let templateContext = { $implicit: rowData, index: rowIndex - first() };
                                <ng-container *ngTemplateOutlet="template; context: templateContext" />
                            } @else {
                                @if (cell.id && cell.type === 'property') {
                                    {{ rowData[cell.id] }}
                                }
                            }
                        </td>
                    }
                }
            </tr>
        </ng-template>

        <ng-template #emptymessage>
            <tr>
                <td [attr.colspan]="displayedColumns().length + (showSelectionColumn() ? 1 : 0)" [attr.data-test]="`${tableKey()}-empty-tableCell`">
                    <div class="tw-text-gray-500 tw-text-center tw-py-14 tw-font-[300] tw-text-xl">
                        @let message = searchText()
                            ? emptySearchMessage()
                            : emptyMessage();
                        {{ message }}
                    </div>
                </td>
            </tr>
        </ng-template>

        <ng-template pTemplate="paginatordropdownitem" let-option>
            <span [attr.data-test]="`${tableKey()}-pagination-option-` + option.value">{{ option.label }}</span>
        </ng-template>

        <ng-template pTemplate="paginatorfirstpagelinkicon">
            <span class="pi pi-angle-double-left" [attr.data-test]="`${tableKey()}-pagination-first-page`"></span>
        </ng-template>

        <ng-template pTemplate="paginatorprevpagelinkicon">
            <span class="pi pi-angle-left" [attr.data-test]="`${tableKey()}-pagination-prev-page`"></span>
        </ng-template>

        <ng-template pTemplate="paginatornextpagelinkicon">
            <span class="pi pi-angle-right" [attr.data-test]="`${tableKey()}-pagination-next-page`"></span>
        </ng-template>

        <ng-template pTemplate="paginatorlastpagelinkicon">
            <span class="pi pi-angle-double-right" [attr.data-test]="`${tableKey()}-pagination-last-page`"></span>
        </ng-template>
    </p-table>
</div>

